import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import { IDashboardListRow } from 'components/foundation/dashboard/dashboard-list/DashboardList/DashboardList';
import { useDashboardTimeRange } from 'containers/dashboard/hooks';
import { convertTotalsToDashboardListRow, getDashboardTimeFrameTitle } from 'containers/dashboard/util/utils';
import { gql } from 'graphql-request';
import { useAsync } from 'hooks';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import { useCallback, useEffect, useMemo } from 'react';
import { getFullNameFromMember } from 'utils/stringUtils';

interface IActiveMemberRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  totalSeconds: number;
}

export const ACTIVE_MEMBERS_DASHBOARD_QUERY = gql`
  query ActiveMembersDashboard($start: Date!, $end: Date!, $first: Int!) {
    dashboard {
      activeMembers(start: $start, end: $end, first: $first) {
        id
        firstName
        lastName
        totalSeconds
      }
    }
  }
`;

const TOP_MEMBER_COUNT = 5;

export function useActiveMemberMainDashboardData(setting: IDashboardSettingsItem) {
  const graphQLClient = useGraphQLClient();
  const timeFrame = setting.options?.time ?? 'pastFourteen';
  const timeRange = useDashboardTimeRange(timeFrame, 'system');

  const getData = useCallback(async (): Promise<Array<{ id: string; title: string; total: number }>> => {
    const result = await graphQLClient.request<{ dashboard: { activeMembers: IActiveMemberRow[] } }>({
      document: ACTIVE_MEMBERS_DASHBOARD_QUERY,
      variables: {
        start: timeRange.startTime.toISODate(),
        end: timeRange.endTime.toISODate(),
        first: TOP_MEMBER_COUNT,
      },
    });

    return result.dashboard.activeMembers.map((member) => ({
      id: member.id,
      title: getFullNameFromMember(member),
      total: member.totalSeconds,
    }));
  }, [graphQLClient, timeRange.startTime, timeRange.endTime]);

  const { execute, loading, error, data } = useAsync(getData, false);
  const rowData: IDashboardListRow[] = useMemo(() => convertTotalsToDashboardListRow(data ?? []), [data]);

  useEffect(() => {
    execute();
  }, [execute, timeFrame]);

  const title = getDashboardTimeFrameTitle(setting.title, timeFrame);
  return { data: rowData, title, setting, loading, error, execute };
}
